import React, { useState, useEffect } from 'react';
import AdminLayoutWrapper from '../../components/layout/AdminLayoutWrapper';
import DataTableLayout from '../../components/common/DataTableLayout';
import FilterPanel from '../../components/common/FilterPanel';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import {
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from '@mui/x-data-grid';
import { ACADEMIC_YEARS, STATUSES } from '../../config/Config';
import { useNavigate } from 'react-router-dom';
import { createFilterChangeHandler, clearFilters, filterData, dataGridProps } from '../../utils/tableUtils';
import api from '../../services/api';

export default function AcademicYearManagementPage() {
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ year: '' });
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ year: '', status: '' });
  const [statusOptions, setStatusOptions] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchPaginated = async (endpoint) => {
    let url = endpoint;
    let all = [];
    while (url) {
      const res = await api.get(url);
      const data = res.data;
      if (Array.isArray(data)) {
        all = data;
        break;
      }
      all = all.concat(data.results || []);
      url = data.next || null;
    }
    return all;
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const allYears = await fetchPaginated(ACADEMIC_YEARS);
      setYears(allYears);
    } catch (err) {
      console.error('Error fetching academic years:', err);
      showSnackbar('Failed to load academic years', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const res = await api.get(STATUSES);
        const list = Array.isArray(res.data) ? res.data : (res.data?.results || []);
        const draftPublished = list.filter(s =>
          (s.status_id || s.statusId || '').toUpperCase() === 'DRAFT' ||
          (s.status_id || s.statusId || '').toUpperCase() === 'PUBLISHED'
        );
        setStatusOptions(draftPublished);
      } catch (err) {
        setStatusOptions([]);
      }
    };
    fetchStatuses();
  }, []);

  const handleOpen = (item = null) => {
    if (item) {
      setForm({ year: item.year, status: item.status || '' });
      setEditMode(true);
      setCurrentId(item.id);
    } else {
      setForm({ year: '', status: '' });
      setEditMode(false);
      setCurrentId(null);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setForm({ year: '', status: '' });
    setEditMode(false);
    setCurrentId(null);
  };

  const handleSubmit = async () => {
    if (!form.year) {
      showSnackbar('Please enter a year', 'error');
      return;
    }

    try {
      const payload = { year: form.year };
      if (form.status) payload.status = Number(form.status);
      if (editMode) {
        await api.put(`${ACADEMIC_YEARS}${currentId}/`, payload);
        showSnackbar('Academic year updated successfully');
      } else {
        await api.post(ACADEMIC_YEARS, payload);
        showSnackbar('Academic year created successfully');
      }
      handleClose();
      fetchAll();
    } catch (err) {
      console.error('Error saving academic year:', err);
      showSnackbar('Failed to save academic year', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this academic year?')) return;
    try {
      await api.delete(`${ACADEMIC_YEARS}${id}/`);
      showSnackbar('Academic year deleted successfully');
      fetchAll();
    } catch (err) {
      console.error('Error deleting academic year:', err);
      showSnackbar('Failed to delete academic year', 'error');
    }
  };

  const filteredYears = filterData(years, filters);

  const handleFilterChange = createFilterChangeHandler(setFilters);
  const handleClearFilters = () => clearFilters(setFilters, { year: '' });

  const filterConfig = [
    {
      field: 'year',
      label: 'Year',
      type: 'text',
      placeholder: 'Search by year...'
    }
  ];

  const columns = [
    {
      field: 'year',
      headerName: 'Academic Year',
      flex: 2,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          size="small" 
          color="primary" 
          variant="outlined"
        />
      )
    },
    {
      field: 'status_value',
      headerName: 'Status',
      flex: 1,
      renderCell: (params) => params.value ? (
        <Chip label={params.value} size="small" variant="outlined" />
      ) : '-'
    },
    {
      field: 'created_at',
      headerName: 'Created',
      flex: 1,
      valueGetter: (params) => params.value ? new Date(params.value).toLocaleDateString() : 'N/A'
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleOpen(params.row)}
              sx={{ p: 0.5 }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDelete(params.row.id)}
              sx={{ p: 0.5, color: 'error.main' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  const actionButtons = [
    {
      label: 'Add Academic Year',
      icon: <AddIcon />,
      variant: 'contained',
      onClick: () => handleOpen(null)
    }
  ];

  const emptyStateProps = {
    title: 'No academic years found',
    description: 'Try adjusting your filters or add your first academic year.',
    icon: <AddIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
  };

  return (
    <AdminLayoutWrapper>
      <DataTableLayout
        title="Academic Years"
        actionButtons={actionButtons}
        filterPanel={
          <FilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            filterConfig={filterConfig}
          />
        }
        loading={loading}
        isEmpty={!loading && filteredYears.length === 0}
        emptyStateProps={emptyStateProps}
        mobileFilterOpen={mobileFilterOpen}
        onMobileFilterClose={() => setMobileFilterOpen(!mobileFilterOpen)}
      >
        <DataGrid
          rows={filteredYears}
          columns={columns}
          {...dataGridProps}
          components={{
            Toolbar: () => (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2,
                p: 1
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GridToolbarColumnsButton 
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  />
                  <GridToolbarDensitySelector 
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  />
                  <GridToolbarExport 
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  />
                </Box>
              </Box>
            )
          }}
        />
      </DataTableLayout>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Academic Year' : 'Add Academic Year'}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Academic Year"
            value={form.year}
            onChange={(e) => setForm({ ...form, year: e.target.value })}
            fullWidth
            margin="normal"
            required
            autoFocus
            placeholder="e.g., 5, 6, 7"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={form.status}
              label="Status"
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              <MenuItem value="">None</MenuItem>
              {statusOptions.map((s) => (
                <MenuItem key={s.id} value={String(s.id)}>
                  {s.value || s.status_id || s.statusId}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminLayoutWrapper>
  );
}
